import './MembershipInfo.css'

import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Divider } from '@mui/material';
import { LoginContext } from '../../../../App';
import { OrganizationDetailsContext } from '../../forms/OrganizationDetailsModal';
import { enableDisableUserMembership, updateUserMembership, createUserMembership, removeUserMembership, createUserMembershipByEmail } from '../../../../services/b2b-users-apiService';
import MembershipRoles from './MembershipRoles';
import { ContentCut, Save } from '@mui/icons-material';

import NewMemberByEmailFields from './NewMemberByEmailFields';
import NewMemberFields from './NewMemberFields';
import ExistingMemberFields from './ExistingMemberFields';
import B2BFormContainer from '../../../b2b-common/B2BFormContainer';

const MembershipInfo = React.forwardRef(({ initialMember, isNewMember, isNewMemberByEmail, onMemberUpdate, onMemberRemove, onClose }, ref) => {
    const { loginData } = useContext(LoginContext);
    const { organizationUUID, organizationRoles, organizationId } = useContext(OrganizationDetailsContext);
    const navigate = useNavigate();

    const [member, setMember] = useState(initialMember);
    const [loadingEnableDisableMember, setLoadingEnableDisableMember] = useState(false);
    const [loadingSaveMembershipInfo, setLoadingSaveMembershipInfo] = useState(false);
    const [loadingRemoveMember, setLoadingRemoveMember] = useState(false);

    useEffect(() => {
        if (!loginData || !organizationUUID) {
            navigate('/login');
        }
    }, [loginData, organizationUUID, navigate]);

    useEffect(() => {
        setMember(initialMember);
    }, [initialMember]);

    const handleFieldChange = (fieldName, value) => {
        setMember((prevMember) => {
            if (fieldName === 'roles') {
                return {
                    ...prevMember,
                    roles: value,
                };
            }
            return {
                ...prevMember,
                user: {
                    ...prevMember.user,
                    [fieldName]: value,
                },
            };
        });
    };

    const handleSwitchToggle = async () => {
        try {
            const userId = member.userId;
            const { accessToken } = loginData;
            setLoadingEnableDisableMember(true);
            const updatedMember = await enableDisableUserMembership(organizationUUID, userId, !member.enabled, accessToken);
            // Check if the updated member has the expected structure
            if (updatedMember && updatedMember.user) {
                setMember(updatedMember);
                onMemberUpdate(updatedMember);
            } else {
                console.error('Invalid data structure returned:', updatedMember);
                alert('Failed to update membership. Please try again.');
            }
        } catch (error) {
            console.error('Failed to toggle membership:', error.message);
            alert('Failed to toggle membership. Please try again.');
        } finally {
            setLoadingEnableDisableMember(false);
        }
    };

    const handleCreateMembership = async () => {
        try {
            const { accessToken } = loginData;
            setLoadingSaveMembershipInfo(true);

            const newMemberPayload = {
                firstName: member.user.firstName,
                lastName: member.user.lastName,
                email: member.user.email,
                password: member.user.password, // Set your default password
                locale: member.user.locale,
                phoneNumber: member.user.phoneNumber,
                userOrganizations: [
                    {
                        organizationId: organizationId,
                        roles: member.roles,
                    },
                ],
            };
            const newUser = await createUserMembership(organizationUUID, newMemberPayload, accessToken);
            if (newUser && newUser.userOrganizations) {
                const userOrganization = newUser.userOrganizations.find(org => org.organizationId === organizationId);
                if (userOrganization) {
                    const newUserCopy = { ...newUser, userOrganizations: undefined };
                    userOrganization.user = newUserCopy;
                    setMember(userOrganization);
                    onMemberUpdate(userOrganization);
                    onClose();
                } else {
                    console.error('User organization with organizationId 2 not found.');
                }
            } else {
                console.error('Invalid data structure returned:', newUser);
                alert('Failed to create membership. Please try again.');
            }
        } catch (error) {
            console.error('Failed to create user:', error.message);
            alert('Failed to create user. Please try again.');
        } finally {
            setLoadingSaveMembershipInfo(false);
        }
    }

    const handleUpdateMembership = async () => {
        try {
            const userId = member.userId;
            const { accessToken } = loginData;
            setLoadingSaveMembershipInfo(true);

            const updatedMember = await updateUserMembership(organizationUUID, userId, member, accessToken);
            // Check if the updated member has the expected structure
            if (updatedMember && updatedMember.user) {
                setMember(updatedMember);
                onMemberUpdate(updatedMember);
                onClose();
            } else {
                console.error('Invalid data structure returned:', updatedMember);
                alert('Failed to update membership. Please try again.');
            }
        } catch (error) {
            console.error('Failed to toggle membership:', error.message);
            alert('Failed to toggle membership. Please try again.');
        } finally {
            setLoadingSaveMembershipInfo(false);
        }
    };

    const handleRemoveMember = async () => {
        try {
            const userId = member.userId;
            const { accessToken } = loginData;
            setLoadingRemoveMember(true);

            await removeUserMembership(organizationUUID, userId, accessToken);
            onMemberRemove(member);
            onClose();
        } catch (error) {
            console.error('Failed to remove membership:', error.message);
            alert('Failed to remove membership. Please try again.');
        } finally {
            setLoadingRemoveMember(false);
        }

    }

    const handleCreateMembershipByEmail = async () => {
        try {
            const userEmail = member.user.email;
            const { accessToken } = loginData;
            setLoadingSaveMembershipInfo(true);
            const newMember = await createUserMembershipByEmail(organizationUUID, userEmail, 'USER', accessToken);

            if (newMember && newMember.userOrganizations) {
                const userOrganization = newMember.userOrganizations.find(org => org.organizationId === organizationId);
                if (userOrganization) {
                    const newUserCopy = { ...newMember, userOrganizations: undefined };
                    userOrganization.user = newUserCopy;
                    setMember(userOrganization);
                    onMemberUpdate(userOrganization);
                    onClose();
                } else {
                    console.error('User organization with organizationId not found.');
                }
            } else {
                console.error('Invalid data structure returned:', newMember);
                alert('Failed to create membership. Please try again.');
            }
        } catch (error) {
            console.error('Failed to add new member by email:', error.message);
            alert('Failed to add new member by email. Please try again.');
        } finally {
            setLoadingSaveMembershipInfo(false);
        }
    }

    const buttonsConfig = [
        {
            label: 'Remove Member',
            confirm: true,
            onConfirm: handleRemoveMember,
            isLoading: loadingRemoveMember,
            color: 'secondary',
            variant: 'contained',
            key: '2',
            icon: <ContentCut />,
        },
        {
            label: 'Save & Close',
            onClick: isNewMember ? handleCreateMembership : isNewMemberByEmail ? handleCreateMembershipByEmail : handleUpdateMembership,
            isLoading: loadingSaveMembershipInfo,
            color: 'primary',
            variant: 'contained',
            key: '1',
            icon: <Save />,
        },
    ];

    return (
        <B2BFormContainer ref={ref} title={`${member?.user?.firstName || 'New'} ${member?.user?.lastName || 'Member'}`} onClose={onClose} buttonsConfig={buttonsConfig}>
            {isNewMember ? (
                <NewMemberFields member={member} handleFieldChange={handleFieldChange} />
            ) : isNewMemberByEmail ? (
                <NewMemberByEmailFields member={member} handleFieldChange={handleFieldChange} />
            ) : (
                <ExistingMemberFields
                    member={member}
                    loadingEnableDisableMember={loadingEnableDisableMember}
                    handleSwitchToggle={handleSwitchToggle}
                />
            )}
            <Divider orientation='horizontal' flexItem variant='fullWidth' />
            <div className="membershipInfoRoles">
                <MembershipRoles
                    organizationRoles={organizationRoles}
                    memberRoles={member.roles}
                    onUpdateRoles={(roles) => {
                        setMember({ ...member, roles });
                    }}
                />
            </div>
        </B2BFormContainer>
    );
});

MembershipInfo.displayName = 'MembershipInfo';

export default MembershipInfo;